import React, { useEffect, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { ImageNotSupported } from '@mui/icons-material';
import api from '../../services/api';

// Photos sit behind the JWT-protected API, so a plain <img src> can't fetch them
// (no way to set an Authorization header). Pull the blob and render an object URL.
// Blobs are cached per photo id so revisiting a list doesn't refetch.
const cache = new Map();

export default function AuthImage({ photoId, alt = '', height = 180, radius = 8, fit = 'cover', sx = {} }) {
  const [url, setUrl] = useState(() => cache.get(photoId) || '');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let created = '';
    if (!photoId) { setUrl(''); return undefined; }
    if (cache.has(photoId)) { setUrl(cache.get(photoId)); return undefined; }
    setUrl(''); setFailed(false);
    api.get(`/exhibition-products/photos/${photoId}/raw`, { responseType: 'blob' })
      .then(r => {
        if (!alive) return;
        created = URL.createObjectURL(r.data);
        cache.set(photoId, created);
        setUrl(created);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      // Only revoke if this instance created a URL that never reached the cache.
      if (created && cache.get(photoId) !== created) URL.revokeObjectURL(created);
    };
  }, [photoId]);

  if (failed || !photoId) {
    return (
      <Box sx={{
        height, borderRadius: `${radius}px`, bgcolor: 'action.hover',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', ...sx,
      }}>
        <ImageNotSupported fontSize="small" />
      </Box>
    );
  }
  if (!url) return <Skeleton variant="rounded" height={height} sx={{ borderRadius: `${radius}px`, ...sx }} />;
  return (
    <Box component="img" src={url} alt={alt}
      sx={{ width: '100%', height, objectFit: fit, borderRadius: `${radius}px`, display: 'block', bgcolor: 'action.hover', ...sx }} />
  );
}
